import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import PoolMap from '@/components/PoolMap';
import LanguageSelector from '@/components/LanguageSelector';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Check, ChevronRight, ChevronLeft, Phone, MapPin, PenTool, Users, Trash2, Plus, CalendarCheck, X } from 'lucide-react';

const LETRAS_DNI = "TRWAGMYFPDXBNJZSQVHLCKE";
function dniValido(v) {
  v = (v || '').toUpperCase().trim();
  let m;
  if ((m = v.match(/^(\d{8})([A-Z])$/)))        return LETRAS_DNI[+m[1] % 23] === m[2];
  if ((m = v.match(/^([XYZ])(\d{7})([A-Z])$/))) return LETRAS_DNI[+("XYZ".indexOf(m[1]) + m[2]) % 23] === m[3];
  return false;
}

// El registro finaliza al aceptar los consentimientos (sin paso "Confirmar" aparte:
// esa doble confirmación dejaba familias con consents otorgados pero sin completar).
const STEP_KEYS = ['step_family', 'step_phone', 'step_pool', 'Actividades', 'step_consents'];
const STEP_ICONS = [Users, Phone, MapPin, CalendarCheck, PenTool];

export default function RegistrationWizard() {
  const { api, refreshUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [consents, setConsents] = useState({ c1: false, c2: false, c3: false, c4: false });
  const [showRulesModal, setShowRulesModal] = useState(false);

  // Cuenta atrás anti-duplicados: tras enviar el OTP, el botón queda
  // deshabilitado 60 s (mismo cooldown que aplica el backend).
  const [otpCooldown, setOtpCooldown] = useState(0);
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setTimeout(() => setOtpCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCooldown]);

  // Load existing family data and auto-advance past completed steps
  useEffect(() => {
    const loadExisting = async () => {
      try {
        const { data } = await api.get('/families/me');

        // Si ya completó el alta, redirigir a /family (nunca a /scan — el token puede ser de otro día)
        if (data.registration_complete) {
          navigate('/family', { replace: true });
          return;
        }

        if (data.family_name) setFamilyName(data.family_name);
        if (data.room_number) setFamilyEmail(data.room_number);
        if (data.dni) setDni(data.dni);
        if (data.children?.length > 0) {
          setChildren(data.children.map(c => ({ name: c.name, age: String(c.age || ''), id: c.id })));
        }
        if (data.phone) { setPhone(data.phone); }
        if (data.phone_verified) { setPhoneVerified(true); }
        if (data.pool_location) { setPoolLocation(data.pool_location); }

        // Precargar consentimientos ya otorgados (re-entradas: casillas marcadas)
        if (data.consents) {
          setConsents({
            c1: !!data.consents.consent_datos,
            c2: !!data.consents.consent_participacion,
            c3: !!data.consents.consent_edad,
            c4: !!data.consents.consent_imagen,
          });
        }

        // Auto-advance: saltar los pasos ya completados. Si solo faltan los
        // consentimientos, aterrizar DIRECTAMENTE en esa pantalla (paso final).
        const hasBasicInfo = data.family_name && data.children?.length > 0;
        const hasPhone = data.phone_verified;
        if (hasBasicInfo && hasPhone && data.pool_location) {
          setStep(4); // consentimientos — un toque y terminan
        } else if (hasBasicInfo && hasPhone) {
          setStep(2); // pool map
        } else if (hasBasicInfo) {
          setStep(1); // phone
        }
      } catch (err) {
        console.error('Load family data:', err);
      }
      setInitialLoaded(true);
    };
    loadExisting();
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1: Family info
  const [familyName, setFamilyName] = useState('');
  const [familyEmail, setFamilyEmail] = useState('');
  const [dni, setDni] = useState('');
  const [children, setChildren] = useState([{ name: '', age: '' }]);

  // Memoized computed values
  const childrenNames = useMemo(() => children.filter(c => c.name).map(c => c.name).join(', '), [children]);

  // Step 2: Phone
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [debugCode, setDebugCode] = useState('');

  // Step 3: Pool
  const [poolLocation, setPoolLocation] = useState(null);


  // Step 3b: Activities
  const [todayActivities, setTodayActivities] = useState([]);
  const [dayType, setDayType] = useState('A');
  const [wizardReservations, setWizardReservations] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [selectedChildIds, setSelectedChildIds] = useState([]);

  const addChild = () => setChildren([...children, { name: '', age: '' }]);
  const removeChild = (i) => setChildren(children.filter((_, idx) => idx !== i));
  const updateChild = (i, field, val) => {
    const updated = [...children];
    updated[i] = { ...updated[i], [field]: val };
    setChildren(updated);
  };

  const saveStep1 = useCallback(async () => {
    if (!familyName.trim() || !familyEmail.trim()) {
      toast.error(t('fill_name_room'));
      return false;
    }
    if (dni.trim() && !dniValido(dni)) {
      toast.error('El DNI/NIE introducido no es válido. Revisa el número y la letra de control.');
      return false;
    }
    const validChildren = children.filter(c => c.name.trim() && c.age);
    if (validChildren.length === 0) {
      toast.error(t('add_one_child'));
      return false;
    }
    setSaving(true);
    try {
      await api.put('/families/me', {
        family_name: familyName,
        room_number: familyEmail,
        dni: dni.trim().toUpperCase(),
        children: validChildren.map(c => ({ name: c.name, age: parseInt(c.age, 10) })),
      });
      return true;
    } catch (err) {
      console.error('Save step1 error:', err);
      toast.error('Error al guardar datos');
      return false;
    } finally {
      setSaving(false);
    }
  }, [api, familyName, familyEmail, children, t]);

  const sendOTP = async () => {
    if (!phone.trim()) { toast.error(t('phone_title')); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/auth/send-otp', { phone });
      setOtpSent(true);
      setOtpCooldown(60);
      setDebugCode(data.debug_code || '');
      toast.success(t('sms_sent'));
    } catch (err) {
      if (err.response?.status === 429) setOtpCooldown(60);
      toast.error(err.response?.data?.detail || 'Error al enviar SMS');
    }
    setSaving(false);
  };

  const verifyOTP = async () => {
    if (otp.length < 6) { toast.error('Introduce el codigo de 6 digitos'); return; }
    setSaving(true);
    try {
      await api.post('/auth/verify-otp', { phone, code: otp });
      setPhoneVerified(true);
      toast.success(t('phone_verified'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codigo invalido');
    }
    setSaving(false);
  };

  const saveStep3_pool = async () => {
    if (!poolLocation) { toast.error(t('select_location')); return false; }
    setSaving(true);
    try {
      await api.patch('/families/me/pool-location', poolLocation);
      // Pre-load activities for next step
      await loadActivitiesAndFamily();
      return true;
    } catch (err) { console.error('Save pool error:', err); toast.error(t('error')); return false; }
    finally { setSaving(false); }
  };

  const loadActivitiesAndFamily = useCallback(async () => {
    try {
      const [actRes, famRes, resRes] = await Promise.all([
        api.get('/activities-today'),
        api.get('/families/me'),
        api.get('/reservations'),
      ]);
      setTodayActivities(actRes.data.workshops || []);
      setDayType(actRes.data.day_type || 'A');
      if (famRes.data.children?.length > 0) {
        setChildren(famRes.data.children.map(c => ({ name: c.name, age: String(c.age || ''), id: c.id })));
      }
      setWizardReservations(resRes.data || []);
    } catch (err) {
      console.error('Load activities error:', err);
    }
  }, [api]);

  // Load activities + family (with child UUIDs) when reaching step 3
  useEffect(() => {
    if (step === 3) { loadActivitiesAndFamily(); }
  }, [step, loadActivitiesAndFamily]);

  const bookingWorkshop = useMemo(
    () => todayActivities.find(w => w.id === bookingId),
    [todayActivities, bookingId]
  );

  const isChildDisabledWizard = useCallback((child) => {
    if (!child.id || !bookingWorkshop) return false;
    return wizardReservations.some(r =>
      r.workshop_title === bookingWorkshop.title &&
      r.workshop_date  === bookingWorkshop.date &&
      (r.children || []).some(c => c.id === child.id)
    );
  }, [wizardReservations, bookingWorkshop]);

  const handleWizardBook = async () => {
    if (selectedChildIds.length === 0) { toast.error('Selecciona al menos un niño'); return; }
    setSaving(true);
    try {
      await api.post('/reservations', { workshop_id: bookingId, child_ids: selectedChildIds });
      toast.success('¡Apuntado!');
      setBookingId(null);
      setSelectedChildIds([]);
      const [actRes, resRes] = await Promise.all([
        api.get('/activities-today'),
        api.get('/reservations'),
      ]);
      setTodayActivities(actRes.data.workshops || []);
      setWizardReservations(resRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reservar');
    }
    setSaving(false);
  };

  const saveStep4_consents = async () => {
    if (!consents.c1 || !consents.c2 || !consents.c3) {
      toast.error('Debes aceptar las tres casillas obligatorias para continuar.');
      return false;
    }
    setSaving(true);
    try {
      await api.patch('/families/me/consents', {
        consent_datos: consents.c1,
        consent_participacion: consents.c2,
        consent_edad: consents.c3,
        consent_imagen: consents.c4,
        version: '2026-06-17',
      });
      return true;
    } catch {
      toast.error('Error al guardar consentimientos');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Confirmation data
  const [confirmationData, setConfirmationData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const completeRegistration = async () => {
    setSaving(true);
    try {
      // Reservas ya hechas en el paso 3 — solo marcar el registro como completo
      await api.patch('/families/me/complete');
      const { data } = await api.get('/qr/confirmation');
      setConfirmationData(data);
      await api.post('/qr/complete-session');
      setShowConfirmation(true);
    } catch (err) {
      console.error('Complete registration error:', err);
      toast.error('Error al completar registro');
    }
    setSaving(false);
  };

  const handleNext = async () => {
    let ok = true;
    if (step === 0) ok = await saveStep1();
    else if (step === 1) ok = phoneVerified;
    else if (step === 2) ok = await saveStep3_pool();
    else if (step === 3) ok = true; // activities step - just proceed
    else if (step === 4) {
      // Paso final: aceptar consentimientos Y completar el registro en un solo
      // toque. Sin pantalla "Confirmar" intermedia (dejaba familias a medias).
      ok = await saveStep4_consents();
      if (ok) await completeRegistration();
      return;
    }
    if (ok && step < 4) setStep(step + 1);
    else if (!ok && step === 1 && !phoneVerified) toast.error(t('verify_first'));
  };

  const handlePrev = () => { if (step > 0) setStep(step - 1); };

  // ═══ CONFIRMATION SCREEN (SMS simulado) ═══
  if (showConfirmation && confirmationData) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] py-8 px-4" data-testid="registration-confirmation-screen">
        <div className="max-w-lg mx-auto space-y-6">
          <div className="text-center">
            <img src="/logo.png" alt="Logo" className="h-14 w-auto mx-auto mb-4" />
            <div className="w-16 h-16 rounded-full bg-[#27AE60]/10 flex items-center justify-center mx-auto mb-3">
              <Check size={32} className="text-[#27AE60]" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-manrique-black">Registro Completado</h1>
            <p className="text-manrique-grey mt-1">Confirmacion de inscripcion</p>
          </div>

          {/* Simulated SMS messages */}
          <div className="space-y-3" data-testid="sms-confirmations">
            <p className="text-xs font-medium text-manrique-muted text-center uppercase tracking-wider">SMS de confirmacion (simulado)</p>
            {confirmationData.confirmations.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm space-y-3" data-testid={`sms-confirmation-${i}`}>
                <div className="flex items-center gap-2 text-xs text-manrique-muted">
                  <Phone size={12} /> SMS a {c.phone}
                </div>
                <div className="bg-[#FBF8F3] rounded-xl p-4 text-sm text-manrique-black leading-relaxed">
                  <p className="font-bold text-[#48293A] mb-2">El Club de Manri — Parque Marítimo</p>
                  <p>Confirmado! <span className="font-bold">{c.child_name}</span></p>
                  <p className="mt-1">Actividad: <span className="font-bold">{c.workshop_title}</span></p>
                  <p>Fecha: <span className="font-bold">{c.workshop_date}</span></p>
                  <p>Hora: <span className="font-bold">{c.workshop_time_start} - {c.workshop_time_end}</span></p>
                  <p className="mt-2 text-lg font-heading font-bold text-[#48293A]">
                    Plaza #{c.plaza_number}
                  </p>
                  <p className="text-xs text-manrique-muted mt-2">Familia: {c.family_name} | Tel: {c.phone}</p>
                </div>
              </div>
            ))}
            {confirmationData.confirmations.length === 0 && (
              <div className="card-manrique p-6 text-center text-manrique-muted">
                <p>No hay actividades con registro obligatorio hoy</p>
              </div>
            )}
          </div>

          <div className="bg-[#A8752B]/10 border border-[#A8752B]/20 rounded-2xl p-4 text-center">
            <p className="text-sm font-medium text-[#A8752B]">
              Tu registro ha finalizado. Gracias por inscribirte!
            </p>
            <p className="text-xs text-[#A8752B]/70 mt-1">
              Esta sesion se cerrara automaticamente
            </p>
          </div>

          <button
            onClick={() => {
              localStorage.removeItem('clubdemanri_token');
              sessionStorage.clear();
              window.location.href = '/';
            }}
            className="btn-manrique-outline w-full text-center"
            data-testid="close-session-button"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3] py-8 px-4" data-testid="registration-wizard">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 relative">
          <div className="absolute right-0 top-0"><LanguageSelector variant="minimal" /></div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-manrique-black">
            {t('wizard_title')}
          </h1>
          <p className="text-manrique-grey mt-2">{t('wizard_subtitle')}</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-8 px-2" data-testid="step-indicator">
          {STEP_KEYS.map((key, i) => {
            const Icon = STEP_ICONS[i];
            const done = i < step;
            const active = i === step;
            return (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    done ? 'bg-[#27AE60] text-white' :
                    active ? 'bg-[#48293A] text-white shadow-lg shadow-[#48293A]/20' :
                    'bg-white text-manrique-muted border border-black/10'
                  }`}>
                    {done ? <Check size={18} /> : <Icon size={18} />}
                  </div>
                  <span className={`text-xs hidden sm:block ${active ? 'text-manrique-black font-medium' : 'text-manrique-muted'}`}>
                    {t(key)}
                  </span>
                </div>
                {i < STEP_KEYS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 rounded ${done ? 'bg-[#27AE60]' : 'bg-black/10'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step content */}
        <div className="animate-fade-in" key={step}>

          {/* STEP 1: Family Info */}
          {step === 0 && (
            <div className="card-manrique space-y-6" data-testid="step-family-info">
              <h2 className="font-heading font-bold text-xl text-manrique-black">{t('family_data_title')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1.5">{t('family_name_label')}</label>
                  <input value={familyName} onChange={e => setFamilyName(e.target.value)} placeholder="Familia Garcia" className="input-manrique" data-testid="family-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1.5">{t('email_label')}</label>
                  <input type="email" value={familyEmail} onChange={e => setFamilyEmail(e.target.value)} placeholder="familia@email.com" className="input-manrique" data-testid="family-email-input" />
                </div>
              </div>

              {/* DNI / NIE del tutor */}
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1.5">
                  DNI / NIE del tutor/a
                </label>
                <div className="relative">
                  <input
                    value={dni}
                    onChange={e => setDni(e.target.value.toUpperCase())}
                    placeholder="12345678Z o X1234567Y"
                    maxLength={9}
                    className={`input-manrique pr-10 ${
                      dni && (dniValido(dni) ? 'border-[#27AE60] focus:ring-[#27AE60]' : 'border-red-400 focus:ring-red-400')
                    }`}
                    data-testid="family-dni-input"
                  />
                  {dni && (
                    <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-lg font-bold ${dniValido(dni) ? 'text-[#27AE60]' : 'text-red-500'}`}>
                      {dniValido(dni) ? '✓' : '✕'}
                    </span>
                  )}
                </div>
                {dni && !dniValido(dni) && (
                  <p className="text-xs text-red-500 mt-1.5">
                    DNI/NIE no válido — revisa el número y la letra de control.
                  </p>
                )}
              </div>

              {/* Children - prominent section */}
              <div className="bg-[#FBF8F3] rounded-2xl p-5 sm:p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg text-manrique-black">{t('children_label')}</h3>
                  <span className="text-xs text-manrique-muted">{children.length}/3 max</span>
                </div>
                <div className="space-y-4">
                  {children.map((child, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 border border-black/5 space-y-3" data-testid={`child-card-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-heading font-bold text-sm text-[#48293A]">
                          {i === 0 ? '1er' : i === 1 ? '2do' : '3er'} Hijo/a
                        </span>
                        {children.length > 1 && (
                          <button type="button" onClick={() => removeChild(i)} className="p-1.5 text-manrique-muted hover:text-[#48293A] transition-colors rounded-full hover:bg-[#48293A]/5" data-testid={`remove-child-${i}`}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-manrique-grey mb-1">Nombre completo</label>
                        <input
                          value={child.name}
                          onChange={e => updateChild(i, 'name', e.target.value)}
                          placeholder="Nombre y apellidos del menor"
                          className="input-manrique text-base"
                          data-testid={`child-name-${i}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-manrique-grey mb-1">{t('child_age')}</label>
                        <input
                          type="number"
                          value={child.age}
                          onChange={e => updateChild(i, 'age', e.target.value)}
                          placeholder="Edad"
                          min="0"
                          max="13"
                          className="input-manrique w-32 text-base"
                          data-testid={`child-age-${i}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {children.length < 3 && (
                  <button type="button" onClick={addChild} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-[#48293A]/30 text-[#48293A] font-medium text-sm hover:bg-[#48293A]/5 transition-colors" data-testid="add-child-button">
                    <Plus size={18} /> Registrar otro hijo/a
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: Phone Verification */}
          {step === 1 && (
            <div className="card-manrique space-y-6" data-testid="step-phone-verification">
              <h2 className="font-heading font-bold text-xl text-manrique-black">{t('phone_title')}</h2>
              <p className="text-sm text-manrique-grey">{t('phone_subtitle')}</p>
              {!phoneVerified ? (
                <>
                  <div className="flex gap-3">
                    <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+34 612 345 678" className="input-manrique flex-1" disabled={otpSent} data-testid="phone-input" />
                    {!otpSent && (
                      <button onClick={sendOTP} disabled={saving || otpCooldown > 0} className="btn-manrique whitespace-nowrap" data-testid="send-otp-button">
                        {saving ? t('sending') : otpCooldown > 0 ? `Reenviar en ${otpCooldown}s` : t('send_sms')}
                      </button>
                    )}
                  </div>
                  {otpSent && (
                    <div className="space-y-4">
                      {debugCode && (
                        <div className="bg-[#48293A]/5 border border-[#48293A]/20 rounded-2xl p-4 text-center">
                          <p className="text-sm text-manrique-grey">{t('test_code')}:</p>
                          <p className="font-heading font-bold text-2xl text-[#48293A] mt-1" data-testid="debug-code">{debugCode}</p>
                        </div>
                      )}
                      <div className="flex justify-center">
                        <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                          <InputOTPGroup>
                            {[0,1,2,3,4,5].map(i => (
                              <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl rounded-xl border-black/10" />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                      <button onClick={verifyOTP} disabled={saving || otp.length < 6} className="btn-manrique w-full" data-testid="verify-otp-button">
                        {saving ? t('verifying') : t('verify_code')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3 bg-[#27AE60]/10 rounded-2xl p-6" data-testid="phone-verified-badge">
                  <div className="w-10 h-10 rounded-full bg-[#27AE60] flex items-center justify-center">
                    <Check size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-manrique-black">{t('phone_verified')}</p>
                    <p className="text-sm text-manrique-grey">{phone}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Pool Map */}
          {step === 2 && (
            <div data-testid="step-pool-map">
              <PoolMap value={poolLocation} onChange={setPoolLocation} />
            </div>
          )}

          {/* STEP 4: Activities */}
          {step === 3 && (
            <div className="space-y-4" data-testid="step-activities">
              <div className="text-center mb-2">
                <h2 className="font-heading font-bold text-xl text-manrique-black">Actividades del Dia</h2>
              </div>
              {todayActivities.length === 0 ? (
                <div className="card-manrique text-center py-10 text-manrique-muted">
                  <CalendarCheck size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay actividades programadas para hoy</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {[...todayActivities].sort((a, b) => a.time_start.localeCompare(b.time_start)).map(a => {
                    const isRequired = a.registration_required;
                    const spotsLeft = a.max_capacity - a.current_count;
                    const isFull = spotsLeft <= 0;
                    return (
                      <div key={a.id} className="rounded-3xl overflow-hidden border border-black/5 shadow-sm bg-white" data-testid={`activity-card-${a.id}`}>
                        {/* Colored header */}
                        <div className={`relative px-5 py-6 ${isRequired ? 'bg-gradient-to-br from-[#48293A] to-[#844E33]' : 'bg-gradient-to-br from-[#27AE60] to-[#1E8C4E]'}`}>
                          <div className="flex items-start justify-between">
                            <span className={`text-xs font-bold px-3 py-1 rounded-full ${isRequired ? 'bg-white/20 text-white' : 'bg-white/20 text-white'}`}>
                              {isRequired ? 'Registro obligatorio' : 'Acceso libre'}
                            </span>
                            <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/90 text-manrique-black">
                              {spotsLeft > 0 ? `${spotsLeft} plazas` : 'Completo'}
                            </span>
                          </div>
                          <h3 className="font-heading font-bold text-2xl text-white mt-4 leading-tight">
                            {a.title}
                          </h3>
                        </div>

                        {/* Content */}
                        <div className="px-5 py-5 space-y-4">
                          <p className="text-sm text-manrique-grey leading-relaxed">{a.description}</p>

                          <div className="flex flex-wrap gap-2">
                            <span className="text-xs border border-black/10 rounded-full px-3 py-1 text-manrique-black font-medium">
                              {a.age_min}-{a.age_max} anos
                            </span>
                            <span className={`text-xs rounded-full px-3 py-1 font-medium ${isRequired ? 'bg-[#48293A]/10 text-[#48293A]' : 'bg-[#27AE60]/10 text-[#27AE60]'}`}>
                              {a.time_start} - {a.time_end}
                            </span>
                            <span className="text-xs bg-[#FBF8F3] rounded-full px-3 py-1 text-manrique-muted">
                              Aforo: {a.max_capacity}
                            </span>
                          </div>

                          {/* Warning for under 9 */}
                          {!isRequired && (
                            <div className="bg-[#A8752B]/8 border border-[#A8752B]/20 rounded-xl px-4 py-3">
                              <p className="text-xs text-[#A8752B] leading-relaxed">
                                <span className="font-bold">Por orden de llegada.</span> Los menores de 9 anos deben estar acompanados en todo momento de un adulto mayor de 18 anos durante la actividad.
                              </p>
                            </div>
                          )}

                          {/* Footer */}
                          <div className="pt-2 border-t border-black/5 space-y-2">
                            {(() => {
                              const bookedChildren = wizardReservations
                                .filter(r => r.workshop_title === a.title && r.workshop_date === a.date)
                                .flatMap(r => r.children || []);
                              return (
                                <>
                                  {bookedChildren.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                      {bookedChildren.map(c => (
                                        <span key={c.id} className="text-xs bg-[#27AE60]/10 text-[#27AE60] px-2.5 py-1 rounded-full font-medium">
                                          ✓ {c.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <div className="flex items-center justify-between">
                                    {isRequired ? (
                                      <>
                                        <p className="text-xs text-manrique-muted">
                                          {isFull ? 'Sin plazas disponibles' : `${spotsLeft} de ${a.max_capacity} plazas disponibles`}
                                        </p>
                                        {!isFull && (
                                          <button
                                            onClick={() => { setBookingId(a.id); setSelectedChildIds([]); }}
                                            className="rounded-full px-5 py-2.5 text-sm font-bold bg-[#48293A]/10 text-[#48293A] hover:bg-[#48293A] hover:text-white transition-all"
                                            data-testid={`register-activity-${a.id}`}
                                          >
                                            Reservar plaza
                                          </button>
                                        )}
                                        {isFull && (
                                          <span className="text-sm font-medium text-[#48293A] bg-[#48293A]/5 px-4 py-2 rounded-full">Completo</span>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <p className="text-sm font-bold text-[#27AE60]">Acceso libre</p>
                                        <span className="text-xs text-manrique-muted bg-[#FBF8F3] px-4 py-2 rounded-full">Sin inscripcion</span>
                                      </>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 5: Consentimientos */}
          {step === 4 && (
            <div className="space-y-4" data-testid="step-signatures">
              <div className="text-center">
                <h2 className="font-heading font-bold text-xl text-manrique-black">Consentimiento y autorizaciones</h2>
                <p className="text-sm text-manrique-grey mt-1">Las casillas marcadas con <span className="text-red-600 font-bold">*</span> son obligatorias para continuar.</p>
              </div>

              {/* Casilla 1 — datos (obligatoria, enlaza a /privacidad) */}
              <div className="card-manrique p-5">
                <label className="flex gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.c1}
                    onChange={e => setConsents(p => ({ ...p, c1: e.target.checked }))}
                    className="mt-0.5 w-5 h-5 flex-shrink-0 accent-[#48293A] cursor-pointer rounded"
                    data-testid="consent-1"
                  />
                  <span className="text-sm text-manrique-black leading-relaxed">
                    <span className="text-red-600 font-bold">* </span>
                    Declaro ser el <strong>padre, madre o tutor/a legal</strong> del/de los menor/es que inscribo, y autorizo el tratamiento de mis datos y de los datos del/de los menor/es para la gestión de su participación en las actividades de <strong>El Club de Manri</strong>, conforme a la{' '}
                    <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[#48293A] underline font-medium">
                      Política de Privacidad
                    </a>, que declaro haber leído.
                  </span>
                </label>
              </div>

              {/* Casilla 2 — participación (obligatoria) */}
              <div className="card-manrique p-5">
                <label className="flex gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.c2}
                    onChange={e => setConsents(p => ({ ...p, c2: e.target.checked }))}
                    className="mt-0.5 w-5 h-5 flex-shrink-0 accent-[#48293A] cursor-pointer rounded"
                    data-testid="consent-2"
                  />
                  <span className="text-sm text-manrique-black leading-relaxed">
                    <span className="text-red-600 font-bold">* </span>
                    Autorizo la participación del/de los menor/es en las actividades de ocio y animación infantil de <strong>El Club de Manri</strong> en el Parque Marítimo César Manrique.
                  </span>
                </label>
              </div>

              {/* Casilla 3 — edad (obligatoria) + botón normas */}
              <div className="card-manrique p-5 space-y-3">
                <label className="flex gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.c3}
                    onChange={e => setConsents(p => ({ ...p, c3: e.target.checked }))}
                    className="mt-0.5 w-5 h-5 flex-shrink-0 accent-[#48293A] cursor-pointer rounded"
                    data-testid="consent-3"
                  />
                  <span className="text-sm text-manrique-black leading-relaxed">
                    <span className="text-red-600 font-bold">* </span>
                    Declaro que la edad que he indicado para cada menor es <strong>veraz y correcta</strong> y que corresponde a la exigida para las actividades en las que lo inscribo. Entiendo que algunas actividades establecen una <strong>edad mínima por motivos de seguridad</strong>. Si hubiera facilitado una edad incorrecta y ello permitiera el acceso de un menor a una actividad que no corresponde a su edad, <strong>asumo la responsabilidad por las consecuencias que de ello pudieran derivarse</strong>.
                  </span>
                </label>
                <button
                  onClick={() => setShowRulesModal(true)}
                  className="w-full py-2.5 rounded-2xl text-xs font-bold bg-[#48293A]/8 text-[#48293A] hover:bg-[#48293A]/15 transition-all"
                  data-testid="read-rules-button"
                >
                  Leer normas de comportamiento y acompañamiento
                </button>
              </div>

              {/* Casilla 4 — imagen (opcional) */}
              <div className="card-manrique p-5 space-y-2">
                <label className="flex gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consents.c4}
                    onChange={e => setConsents(p => ({ ...p, c4: e.target.checked }))}
                    className="mt-0.5 w-5 h-5 flex-shrink-0 accent-[#48293A] cursor-pointer rounded"
                    data-testid="consent-4"
                  />
                  <span className="text-sm text-manrique-black leading-relaxed">
                    Autorizo a captar y utilizar <strong>imágenes (fotografías o vídeos)</strong> del/de los menor/es durante las actividades, para la <strong>difusión y promoción de las actividades de El Club de Manri</strong> en redes sociales y con fines comerciales.
                  </span>
                </label>
                <p className="text-xs text-manrique-grey pl-8">
                  Voluntario. Puede revocarse en cualquier momento escribiendo a info@elclubdemanri.es. Denegarlo no impide la participación.
                </p>
              </div>

            </div>
          )}

          {/* Rules Modal */}
          {showRulesModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowRulesModal(false)} data-testid="rules-modal">
              <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="bg-[#48293A] rounded-t-3xl px-6 pt-6 pb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-lg text-white">Normas de Comportamiento y Acompanamiento</h3>
                    <button onClick={() => setShowRulesModal(false)} className="p-1.5 hover:bg-white/20 rounded-full transition-colors" data-testid="close-rules-modal">
                      <span className="text-white text-xl leading-none">&times;</span>
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-5 text-sm text-manrique-black leading-relaxed">
                  <div>
                    <h4 className="font-heading font-bold text-base mb-2">1. Normas Generales</h4>
                    <ul className="list-disc pl-5 space-y-1.5 text-manrique-grey">
                      <li>Los padres/tutores son responsables de sus hijos antes y despues de cada actividad.</li>
                      <li>Es obligatorio respetar los horarios de inicio y fin de cada taller.</li>
                      <li>Los ninos deben llevar ropa y calzado adecuado para cada actividad.</li>
                      <li>Esta prohibido el acceso de alimentos y bebidas a las zonas de talleres (salvo actividades de cocina).</li>
                      <li>El material de los talleres es propiedad del parque y debe tratarse con cuidado.</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-base mb-2">2. Normas de Comportamiento</h4>
                    <ul className="list-disc pl-5 space-y-1.5 text-manrique-grey">
                      <li>Los ninos deben mostrar respeto hacia los monitores, compañeros y el entorno.</li>
                      <li>No se permitiran conductas agresivas, insultos o comportamientos disruptivos.</li>
                      <li>Los monitores se reservan el derecho de excluir temporalmente a un nino si su comportamiento pone en riesgo la seguridad del grupo.</li>
                      <li>En caso de incidencia, se comunicara inmediatamente a los padres/tutores.</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-base mb-2">3. Normas de Acompanamiento</h4>
                    <ul className="list-disc pl-5 space-y-1.5 text-manrique-grey">
                      <li><strong>Menores de 9 anos:</strong> Deben estar acompanados en todo momento por un adulto mayor de 18 anos durante las actividades de acceso libre.</li>
                      <li><strong>De 9 a 13 anos:</strong> Pueden participar de forma autonoma en las actividades con registro obligatorio.</li>
                      <li>En actividades acuaticas, es obligatoria la presencia de un adulto para todos los menores, independientemente de su edad.</li>
                      <li>Los padres/tutores deben estar localizables en todo momento (telefono operativo).</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-base mb-2">4. Seguridad</h4>
                    <ul className="list-disc pl-5 space-y-1.5 text-manrique-grey">
                      <li>Es obligatorio comunicar alergias, intolerancias o condiciones medicas relevantes al monitor.</li>
                      <li>Los ninos deben usar proteccion solar en actividades al aire libre.</li>
                      <li>En caso de emergencia, se actuara segun el protocolo del parque y se contactara con los servicios de emergencia.</li>
                      <li>El parque dispone de botiquin de primeros auxilios y personal formado.</li>
                    </ul>
                  </div>
                  <div className="bg-[#48293A]/5 border border-[#48293A]/15 rounded-2xl p-4">
                    <p className="text-xs text-[#48293A] font-medium">
                      Al aceptar, confirmo que he leido y acepto estas normas de comportamiento y acompanamiento para las actividades de El Club de Manri en el Parque Marítimo César Manrique.
                    </p>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setShowRulesModal(false)}
                    className="btn-manrique w-full"
                    data-testid="accept-rules-button"
                  >
                    He leido las normas
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Booking modal — child selector (same model as ScanPage) */}
          {bookingId && bookingWorkshop && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="wizard-booking-modal">
              <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-bold text-xl text-manrique-black">¿Quién va a esta actividad?</h3>
                  <button onClick={() => setBookingId(null)} className="p-1 hover:bg-[#FBF8F3] rounded-full">
                    <X size={20} />
                  </button>
                </div>
                <p className="text-sm text-manrique-grey mb-5">
                  {bookingWorkshop.title} · {bookingWorkshop.time_start}–{bookingWorkshop.time_end}
                </p>
                <div className="space-y-3 mb-6">
                  {children.filter(c => c.name).map((child, i) => {
                    const disabled = isChildDisabledWizard(child);
                    const checked  = child.id ? selectedChildIds.includes(child.id) : false;
                    return (
                      <label
                        key={child.id || child.name}
                        className={`flex items-center gap-3 p-4 rounded-2xl border transition-all ${
                          disabled
                            ? 'border-black/5 bg-black/3 cursor-not-allowed opacity-60'
                            : checked
                              ? 'border-[#48293A] bg-[#48293A]/5 cursor-pointer'
                              : 'border-black/10 hover:border-black/20 cursor-pointer'
                        }`}
                        data-testid={`wizard-select-child-${i}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled || !child.id}
                          onChange={() => {
                            if (!child.id || disabled) return;
                            setSelectedChildIds(prev =>
                              prev.includes(child.id)
                                ? prev.filter(id => id !== child.id)
                                : [...prev, child.id]
                            );
                          }}
                          className="w-5 h-5 rounded accent-[#48293A]"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-manrique-black font-medium">{child.name}</span>
                          {disabled && (
                            <p className="text-[10px] text-[#27AE60] mt-0.5">
                              Ya apuntado/a a «{bookingWorkshop.title}» hoy
                            </p>
                          )}
                          {!child.id && (
                            <p className="text-[10px] text-manrique-muted mt-0.5">Guarda el paso 1 para poder seleccionar</p>
                          )}
                        </div>
                        <span className="text-sm text-manrique-muted">{child.age} años</span>
                      </label>
                    );
                  })}
                </div>
                <button
                  onClick={handleWizardBook}
                  disabled={selectedChildIds.length === 0 || saving}
                  className="btn-manrique w-full disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="wizard-confirm-booking"
                >
                  {saving
                    ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : 'Confirmar inscripción'
                  }
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            onClick={handlePrev}
            disabled={step === 0}
            className="btn-manrique-outline flex items-center gap-2 disabled:opacity-30"
            data-testid="wizard-prev-button"
          >
            <ChevronLeft size={18} /> {t('previous')}
          </button>
          <button
            onClick={handleNext}
            disabled={saving}
            className={`btn-manrique flex items-center gap-2 ${step === 4 ? 'text-base font-bold px-10' : ''}`}
            data-testid={step === 4 ? 'complete-registration-button' : 'wizard-next-button'}
          >
            {saving
              ? t('saving')
              : step === 4
                ? <>Aceptar y finalizar <Check size={18} /></>
                : <>{t('next')} <ChevronRight size={18} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
